import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';

// Import from generated schemas
const {
  CreateSupplyOrderDto,
  UpdateSupplyOrderDto,
  SupplyOrderFirestoreResponse,
} = schemas;

// Re-export schemas for external use
export const CreateSupplyOrderSchema = CreateSupplyOrderDto;
export const SupplyOrderResponseSchema = SupplyOrderFirestoreResponse;
/**
 * ATL-1966: a line is the line Firestore holds — the product snapshot plus `orderQuantity` (or, from
 * an older bundle, the four-key slim line) — so it is free-form here and validated by the backend
 * (`validatedSupplyOrderLines`). A declared four-key item schema is what reduced every line before.
 */
export const SupplyOrderItemSchema = SupplyOrderFirestoreResponse.shape.items.element;
/**
 * ATL-1956: the generated PATCH body. It replaces a hand-written schema that had no `supplierId`
 * and no `date`, so neither could reach Supabase on an update. ATL-1103's status MAP and
 * `isCompleted` are part of the DTO.
 */
export const UpdateSupplyOrderSchema = UpdateSupplyOrderDto;

export const DeleteResponseSchema = z.object({
  message: z.string(),
}).passthrough();

// Type exports
export type SupplyOrderResponse = z.infer<typeof SupplyOrderResponseSchema>;
export type CreateSupplyOrder = z.infer<typeof CreateSupplyOrderSchema>;
export type UpdateSupplyOrder = z.infer<typeof UpdateSupplyOrderSchema>;
export type SupplyOrderItem = z.infer<typeof SupplyOrderItemSchema>;

// API factory function
export function createSupplyOrdersApi(client: TypedApiClient) {
  return {
    getSupplyOrders: (params?: {
      storeId?: string;
      status?: string;
      /** ATL-986: filter to one supplier (Firestore id or Supabase UUID). */
      supplierId?: string;
      /** ATL-986: `false` is the "active orders only" list. */
      isCompleted?: boolean;
    }): Promise<SupplyOrderResponse[]> => {
      return client.get('/supply-orders', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(SupplyOrderResponseSchema),
      });
    },

    /**
     * Get active (not-yet-completed) supply orders.
     * Served by SuppliesController at /supplies/orders/active; returns the same
     * transformed supply-order shape as the other supply-order endpoints.
     */
    getActiveSupplyOrders: (params?: { storeId?: string }): Promise<SupplyOrderResponse[]> => {
      return client.get('/supplies/orders/active', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(SupplyOrderResponseSchema),
      });
    },

    createSupplyOrder: (data: CreateSupplyOrder): Promise<SupplyOrderResponse> => {
      return client.post('/supply-orders', {
        data,
        requestSchema: CreateSupplyOrderSchema,
        responseSchema: SupplyOrderResponseSchema,
      });
    },

    updateSupplyOrder: (id: string, data: UpdateSupplyOrder): Promise<SupplyOrderResponse> => {
      return client.patch(apiPath`/supply-orders/${id}`, {
        data,
        requestSchema: UpdateSupplyOrderSchema,
        responseSchema: SupplyOrderResponseSchema,
      });
    },

    deleteSupplyOrder: (id: string): Promise<z.infer<typeof DeleteResponseSchema>> => {
      return client.delete(apiPath`/supply-orders/${id}`, {
        responseSchema: DeleteResponseSchema,
      });
    },
  };
}
